'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useMemo, useEffect, useCallback, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/free-mode';
import ProductCardHome from '../products/ProductCardHome';
import { GET_NEW_ARRIVALS } from '@/graphql/queries';
import { AWS_URL } from '@/constants';
import defaultImage from '../../images/default.jpg';

const ITEMS_PER_PAGE = 20;

const getProductUrl = (data) => {
  if (data && data.length) {
    const media = data[0]?.media;
    if (media?.path) return AWS_URL && media?.path ? `${AWS_URL}${media.path}` : defaultImage;
  }
  return defaultImage;
};

function mapProductToCard(item, currencySymbol, conversionRate, isINR, selectedCurrency) {
  const product = item.product;
  console.log("mapProductToCard running for", product.id, "variations:", product.product_variations?.length)
  const totalStock =
    product.product_variations?.reduce((sum, variation) => {
      return sum + (variation.product_variant_sizes_aggregate?.aggregate?.sum?.quantity ?? 0);
    }, 0) || undefined;
  console.log("total", totalStock)
  // Display variations (thumbnails) from the view's variation field
  const productVariations = item.product_variations
    ? Array.isArray(item.product_variations)
      ? item.product_variations
      : [item.product_variations]
    : [];
  const productVariantSizes = item.product_variant_sizes
    ? Array.isArray(item.product_variant_sizes)
      ? item.product_variant_sizes
      : [item.product_variant_sizes]
    : [];

  // ✅ FIX: Prioritize the specific variation's media over the parent product media
  const specificVariation = productVariations[0]; // The variation tied to this new arrival item
  const hasVariationMedia =
    specificVariation &&
    Array.isArray(specificVariation.product_media) &&
    specificVariation.product_media.length > 0;

  const hasProductMedia =
    Array.isArray(product.product_media) && product.product_media.length > 0;

  const imageUrl = hasVariationMedia
    ? getProductUrl(specificVariation.product_media)  // ← use variation-specific image first
    : hasProductMedia
      ? getProductUrl(product.product_media)            // ← fallback to product image
      : defaultImage;

  const images = [imageUrl];


  const convertPrice = (price) => {
    if (!price) return null;
    return isINR ? parseFloat(price.toFixed(2)) : parseFloat((price / conversionRate).toFixed(2));
  };

  return {
    id: product.id,
    title: product.name,
    name: product.name,
    category: product.category?.name || '',
    category_id: product.category?.id,
    price: convertPrice(product.price),
    price_per_gm: null,
    images,
    href: `/product-detail/${product.id}`,
    rating: 4,
    dim_weight: null,
    dim_length: null,
    dim_width: null,
    product_code: product.item_code,
    description: null,
    sub_category: product.sub_category,
    min_order_quantity: null,
    product_media: product.product_media,
    product_total_stock: totalStock,
    currencySymbol: isINR
      ? currencySymbol
      : `${selectedCurrency?.name?.toUpperCase() || ''} ${currencySymbol}`.trim(),
    conversionRate,
    status: product.status,
    has_variation: productVariations.length > 0,
    product_variations: productVariations,
  };
}

function NewArrivals() {
  const router = useRouter();
  const swiperRef = useRef(null); // ← no TypeScript type annotation

  const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
  const conversionRate = selectedCurrency?.conversion_rate || 1;
  const currencySymbol = selectedCurrency?.symbol || '₹';
  const isINR =
    selectedCurrency?.name?.toLowerCase() === 'inr' ||
    selectedCurrency?.code?.toLowerCase() === 'inr';

  const whereConditions = {};
  const offset = 0;

  const { loading, data } = useQuery(GET_NEW_ARRIVALS, {
    variables: {
      where: whereConditions,
      limit: ITEMS_PER_PAGE,
      offset,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const apiNewArrivals = data?.product_new_arrivals || [];

  useEffect(() => {
    router.prefetch('/new-arrivals');
  }, [router]);

  const handleMouseEnter = useCallback(() => {
    router.prefetch('/new-arrivals');
  }, [router]);

  const products = useMemo(() => {
    if (!apiNewArrivals.length) return [];
    return apiNewArrivals.map((item) =>
      mapProductToCard(item, currencySymbol, conversionRate, isINR, selectedCurrency)
    );
  }, [apiNewArrivals, currencySymbol, conversionRate, isINR, selectedCurrency]);

  if (loading || products.length === 0) return null;

  return (
    <section className="w-full py-6 md:py-8">

      {/* Header */}
      <div className="w-full px-4 md:px-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4 md:mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="block h-px w-8 bg-[#bf875d]" aria-hidden="true" />
              <span
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-[10px] font-semibold tracking-[0.3em] text-[#bf875d] uppercase"
              >
                Just Arrived
              </span>
            </div>
            <h2
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              className="text-3xl md:text-4xl font-light text-[#1a1a1a] leading-tight"
            >
              New <em className="italic text-[#bf875d]">Arrivals</em>
            </h2>
          </div>

          {/* Prev / Next arrows */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              className="group inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#d4b896] text-[#bf875d] transition-all duration-300 hover:bg-[#bf875d] hover:border-[#bf875d] hover:text-white hover:shadow-lg hover:shadow-[#bf875d]/30"
            >
              <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
            </button>
            <button
              onClick={() => swiperRef.current?.slideNext()}
              className="group inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#d4b896] text-[#bf875d] transition-all duration-300 hover:bg-[#bf875d] hover:border-[#bf875d] hover:text-white hover:shadow-lg hover:shadow-[#bf875d]/30"
            >
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>

        {/* Gold rule */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#d4b896] to-transparent" />
          <div className="w-1.5 h-1.5 bg-[#bf875d] rotate-45 flex-shrink-0" />
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#d4b896] to-transparent" />
        </div>
      </div>

      {/* Swiper */}
      <div className="w-full pl-4 md:pl-5">
        <Swiper
          modules={[FreeMode]}
          freeMode={true}
          grabCursor={true}
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
          slidesPerView={1.5}
          spaceBetween={16}
          breakpoints={{
            480: { slidesPerView: 2.2, spaceBetween: 16 },
            640: { slidesPerView: 3, spaceBetween: 20 },
            768: { slidesPerView: 3.5, spaceBetween: 20 },
            1024: { slidesPerView: 4.5, spaceBetween: 24 },
            1280: { slidesPerView: 5.2, spaceBetween: 28 },
          }}
        >
          {products.map((product) => (
            <SwiperSlide key={product.id}>
              <ProductCardHome product={product} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {/* Bottom CTA */}
      <div className="w-full px-4 md:px-5 mt-5 flex justify-center">
        <Link
          href="/new-arrivals"
          prefetch={true}
          onMouseEnter={handleMouseEnter}
          className="group inline-flex items-center gap-2 border border-[#d4b896] text-[#64483c] text-xs font-medium tracking-widest uppercase px-6 py-3 rounded-full transition-all duration-300 hover:bg-[#bf875d] hover:border-[#bf875d] hover:text-white"
        >
          View All New Arrivals
        </Link>
      </div>

    </section>
  );
}

export default function NewArrivalsLayout() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');
      `}</style>
      <NewArrivals />
    </>
  );
}